import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { format } from "date-fns";
import Swal from "sweetalert2";
import { useModal } from "../../shared/hooks/useModal";
import Modal from "../../components/Modal/Modal";
import {
  Container,
  TabContainer,
  TabNavigationTab,
  CardWrapper,
  ExportToolbar,
} from "./BillingStyles";

import Card from "../../components/common/Card";
import Tab from "../../components/common/Tab/Tab";
import InvoiceTable from "../../components/BillingComponents/InvoiceTable";
import PayoutTable from "../../components/BillingComponents/PayoutTable";
import DateRangePickerComponent from "../../components/common/DateRangePickerComponent";

import { convertToLocalCurrency } from "../../utils/currency"

import {
  getTopBoard,
  getPaymentTopBoard,
  getPayoutTopBoard,
  createAccountDetails,
  getAccountDetails,
  exportAdvertiserPayments,
  exportPublisherPayouts,
} from "../../redux/slices/billingSlice";

const Billing = () => {
  const { showModal, hideModal, isModalOpen } = useModal();
  const dispatch = useDispatch();
  const { accountDetails, paymentTopBoard, topBoard, payoutTopBoard, loading } = useSelector((state) => state.billing);
  const [formData, setFormData] = useState({
    accountNumber: '',
    bankName: '',
    accountName: '',
  }); 
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return {
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: now,
    };
  });
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
      dispatch(getTopBoard());
      dispatch(getPaymentTopBoard());
      dispatch(getPayoutTopBoard());
      dispatch(getAccountDetails());
    }, [dispatch]);

  const handleClick = () => {
    showModal("accountDetails");
    dispatch(getAccountDetails());
    setFormData({
      accountNumber: accountDetails?.accountNumber,
      bankName: accountDetails?.bankName,
      accountName: accountDetails?.accountName,
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    // console.log(formData)
    // return
    e.preventDefault();
    dispatch(createAccountDetails(formData)).then(() => {
      hideModal("accountDetails")
      dispatch(getAccountDetails());
      setFormData({
        accountNumber: accountDetails?.accountNumber || '',
        bankName: accountDetails?.bankName || '',
        accountName: accountDetails?.accountName || '',
      });
    });
  };


  const initState = {
    bills: true,
    advertisers: false,
    publishers: false,
  };
  
  const [tab, setTab] = useState(initState);

  const handleExport = async (type) => {
    const startDate = format(dateRange.startDate, "yyyy-MM-dd");
    const endDate = format(dateRange.endDate, "yyyy-MM-dd");
    const exportAction = type === "publishers"
      ? exportPublisherPayouts
      : exportAdvertiserPayments;
    const fileName = type === "publishers"
      ? "publishers-payout.csv"
      : "advertisers-payment.csv";

    try {
      setExportLoading(true);
      const response = await dispatch(exportAction({ startDate, endDate })).unwrap();
      const fileUrl = response?.data?.file;

      if (!fileUrl) {
        await Swal.fire({
          title: "Export Status",
          text: response?.data?.message || response?.message || "No file available for download",
          icon: "info",
          confirmButtonColor: "#3730a3",
        });
        return;
      }

      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      Swal.fire({
        title: "Export Failed",
        text: error?.message || "Failed to export billing data",
        icon: "error",
        confirmButtonColor: "#3730a3",
      });
    } finally {
      setExportLoading(false);
    }
  };

  const renderExportToolbar = (type) => (
    <ExportToolbar>
      <DateRangePickerComponent
        dateRange={dateRange}
        onDateChange={setDateRange}
      />
      <button
        type="button"
        className="export-button"
        onClick={() => handleExport(type)}
        disabled={exportLoading}
      >
        {exportLoading ? "Exporting..." : "Export CSV"}
      </button>
    </ExportToolbar>
  );

  return (
    <Container>
      <div className="header">
        <div className="texts">
          <h3>Payments</h3>
          <p>
            Manage transaction history, payment approvals, and billing issues
          </p>
        </div>

        <div className="link-buttons">
        <button className="see-more" onClick={handleClick}>
            Set Account Details
          </button>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen("accountDetails")}
        label="Account Details"
        handleClose={() => hideModal("accountDetails")}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Bank Name</label>
            <input
              type="text"
              name="bankName"
              value={formData.bankName}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Account Number</label>
            <input
              type="number"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
            />
          </div>


          <div className="form-group">
            <label>Account Name</label>
            <input
              type="text"
              name="accountName"
              value={formData.accountName}
              onChange={handleChange}
            />
          </div>

          <div className="submit">
          <div></div>
          <div className="submit-buttons">
            <button className="add-campaign" type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Details"}
            </button>
          </div>
        </div>

          {/* <button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Settings"}
          </button> */}
        </form>
      </Modal>
      <>
        <TabContainer>
          <TabNavigationTab>
            <ul>
              <li>
                <a
                  href="#bills"
                  className={tab.bills ? "active" : ""}
                  onClick={() =>
                    setTab((tab) => ({ ...initState, bills: true }))
                  }
                >
                  Advertisers Bill
                </a>
              </li>
              <li>
                <a
                  href="#advertisers"
                  className={tab.advertisers ? "active" : ""}
                  onClick={() =>
                    setTab((tab) => ({
                      ...initState,
                      bills: false,
                      advertisers: true,
                      publishers: false,
                    }))
                  }
                >
                  Advertisers Payment
                </a>
              </li>
              <li>
                <a
                  href="#publishers"
                  className={tab.publishers ? "active" : ""}
                  onClick={() =>
                    setTab((tab) => ({
                      ...initState,
                      bills: false,
                      advertisers: false,
                      publishers: true,
                    }))
                  }
                >
                  Publishers Payout
                </a>
              </li>
            </ul>
          </TabNavigationTab>
        </TabContainer>

        <div className="tab-content">
        {tab.bills && (
            <>
              <CardWrapper>
                <Card text={"Total bills"} totalCount={topBoard?.totalBills || 0} />
                <Card text={"Expected Income"} totalCount={convertToLocalCurrency(topBoard?.expectedIncome || 0)} />
                <Card text={"Current Income"} totalCount={convertToLocalCurrency(topBoard?.currentIncome || 0)} />
              </CardWrapper>
              <InvoiceTable />
            </>
          )}
          {tab.advertisers && (
            <>
              <CardWrapper>
                <Card
                  text={"Total no of payments"}
                  totalCount={paymentTopBoard?.totalPayments}
                />
                <Card
                  text={"Pending payments"}
                  totalCount={paymentTopBoard.pendingPayments}
                />
                {/* <Card text={"Pending advertisers"} totalCount={"100"} /> */}
              </CardWrapper>
              {renderExportToolbar("advertisers")}
              <Tab billing />
            </>
          )}

          {tab.publishers && (
            <>
              <CardWrapper>
                <Card text={"Total payouts"} totalCount={payoutTopBoard?.totalPayouts} />
                <Card text={"Pending payouts"} totalCount={payoutTopBoard?.pendingPayouts} />
                <Card text={"Total payout amount"} totalCount={convertToLocalCurrency(payoutTopBoard?.totalAmount)} />
              </CardWrapper>
              {renderExportToolbar("publishers")}
              <PayoutTable />
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default Billing;
